import React, { useEffect, useRef, useState } from 'react';
import { CircleDollarSign, Clock3, Loader2, Send } from 'lucide-react';
import { siteContent } from '../content/siteContent';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const Contact = () => {
  const sectionRef = useRef(null);
  const { toast } = useToast();
  const { contact, contactSection } = siteContent;
  const contactEmail = process.env.REACT_APP_CONTACT_EMAIL || contact.email;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    projectType: '',
    timeline: '',
    budget: '',
    message: '',
  });

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.fade-in-up');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const subject = `DotCreate inquiry from ${formData.name}`;
      const body = [
        `Name: ${formData.name}`,
        `Email: ${formData.email}`,
        `Company: ${formData.company || '-'}`,
        `Phone: ${formData.phone || '-'}`,
        `Project Type: ${formData.projectType || '-'}`,
        `Timeline: ${formData.timeline || '-'}`,
        `Budget Range: ${formData.budget || '-'}`,
        '',
        'Project Details:',
        formData.message,
      ].join('\n');

      window.location.href = `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      toast({
        title: 'Email Draft Ready',
        description: 'Your email app should open with your message filled in.',
      });
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        projectType: '',
        timeline: '',
        budget: '',
        message: '',
      });
    } catch (error) {
      toast({
        variant: 'destructive',
        title: "Couldn't Open Email App",
        description: 'Please use the email address below to contact us directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" ref={sectionRef} className="relative py-32 overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-96 h-96 bg-[#D22527] rounded-full filter blur-3xl opacity-20"></div>

      <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-16">
          <h2 className="fade-in-up text-5xl md:text-6xl font-bold text-white mb-6">
            {contactSection.heading[0]}
            <br />
            <span className="bg-gradient-to-r from-[#D22527] to-[#88171A] bg-clip-text text-transparent">
              {contactSection.heading[1]}
            </span>
          </h2>
          <p className="fade-in-up text-xl text-gray-400 max-w-3xl mx-auto">{contactSection.intro}</p>
        </div>

        <div className="fade-in-up">
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            {contactSection.reassuranceTags.map((item) => (
              <div
                key={item}
                className="px-4 py-2 rounded-full bg-black/40 border border-[#D22527]/20 text-sm text-gray-300"
              >
                {item}
              </div>
            ))}
          </div>

          <div className="grid lg:grid-cols-[1.35fr_0.8fr] gap-8">
            <div className="p-12 rounded-3xl bg-gradient-to-br from-[#D22527]/10 to-transparent border border-[#D22527]/30 backdrop-blur-sm">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-white font-semibold mb-2">
                      Full Name *
                    </label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="John Doe"
                      className="bg-black/50 border-[#D22527]/30 text-white placeholder:text-gray-500 focus:border-[#D22527] transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-white font-semibold mb-2">
                      Email Address *
                    </label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@company.com"
                      className="bg-black/50 border-[#D22527]/30 text-white placeholder:text-gray-500 focus:border-[#D22527] transition-all duration-300"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="company" className="block text-white font-semibold mb-2">
                      Company Name
                    </label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="Your Company"
                      className="bg-black/50 border-[#D22527]/30 text-white placeholder:text-gray-500 focus:border-[#D22527] transition-all duration-300"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-white font-semibold mb-2">
                      Phone Number
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder={contact.phoneDisplay}
                      className="bg-black/50 border-[#D22527]/30 text-white placeholder:text-gray-500 focus:border-[#D22527] transition-all duration-300"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="projectType" className="block text-white font-semibold mb-2">
                    Project Type *
                  </label>
                  <select
                    id="projectType"
                    name="projectType"
                    required
                    value={formData.projectType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg bg-black/50 border border-[#D22527]/30 text-white focus:border-[#D22527] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#D22527]/20"
                  >
                    <option value="">Select a project type</option>
                    {contactSection.projectTypes.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="timeline" className="block text-white font-semibold mb-2">
                      Timeline
                    </label>
                    <div className="relative">
                      <Clock3 className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D22527]" />
                      <select
                        id="timeline"
                        name="timeline"
                        value={formData.timeline}
                        onChange={handleChange}
                        className="w-full pl-11 pr-4 py-3 rounded-lg bg-black/50 border border-[#D22527]/30 text-white focus:border-[#D22527] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#D22527]/20"
                      >
                        <option value="">Select timeline</option>
                        {contactSection.timelineOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="budget" className="block text-white font-semibold mb-2">
                      Budget Range
                    </label>
                    <div className="relative">
                      <CircleDollarSign className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D22527]" />
                      <select
                        id="budget"
                        name="budget"
                        value={formData.budget}
                        onChange={handleChange}
                        className="w-full pl-11 pr-4 py-3 rounded-lg bg-black/50 border border-[#D22527]/30 text-white focus:border-[#D22527] transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-[#D22527]/20"
                      >
                        <option value="">Select budget range</option>
                        {contactSection.budgetOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="message" className="block text-white font-semibold mb-2">
                    Project Details *
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your goals, the problem you're solving, and what a successful launch looks like."
                    rows={6}
                    className="bg-black/50 border-[#D22527]/30 text-white placeholder:text-gray-500 focus:border-[#D22527] transition-all duration-300 resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#D22527] to-[#88171A] text-white text-lg py-6 hover:shadow-2xl hover:shadow-[#D22527]/50 transition-all duration-300 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed border-0 group"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      {contactSection.submittingLabel}
                    </>
                  ) : (
                    <>
                      {contactSection.submitLabel}
                      <Send className="ml-2 h-5 w-5 group-hover:translate-x-2 transition-transform duration-300" />
                    </>
                  )}
                </Button>
              </form>
            </div>

            <div className="p-8 rounded-3xl bg-gradient-to-br from-black/60 to-[#D22527]/10 border border-[#D22527]/20 backdrop-blur-sm h-full">
              <p className="text-sm font-semibold tracking-[0.2em] uppercase text-[#D22527] mb-4">
                {contactSection.nextStepsTitle}
              </p>
              <div className="space-y-6">
                {contactSection.nextSteps.map((item) => (
                  <div key={item.step} className="flex gap-4">
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-[#D22527] to-[#88171A] text-white font-bold flex items-center justify-center flex-shrink-0">
                      {item.step}
                    </div>
                    <div>
                      <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                      <p className="text-sm text-gray-400 leading-relaxed">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 pt-8 border-t border-[#D22527]/15">
                <p className="text-white font-semibold mb-2">{contactSection.responseExpectationTitle}</p>
                <p className="text-sm text-gray-400 leading-relaxed">{contactSection.responseExpectationText}</p>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-400 mb-4">{contactSection.directContactLabel}</p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              <a href={`mailto:${contactEmail}`} className="text-[#D22527] hover:text-white transition-colors duration-300 text-lg">
                {contactEmail}
              </a>
              <span className="hidden sm:inline text-gray-600">|</span>
              <a href={`tel:${contact.phoneLink}`} className="text-[#D22527] hover:text-white transition-colors duration-300 text-lg">
                {contact.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
